import { useState } from 'react';
import type { ReactNode } from 'react';
import { BottomSheet, Button, FilterGroup } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import type { FilterOption } from '@beautyburst/ui';
import type { SearchSortOption } from '@beautyburst/types';

export type AppliedFilters = Readonly<Record<string, readonly string[]>>;
export type SearchFacet = { key: string; label: string; options: readonly FilterOption[] };

const SORT_OPTIONS: readonly { value: SearchSortOption; label: string }[] = [
  { value: 'RELEVANCE', label: 'Relevance' },
  { value: 'PRICE_ASC', label: 'Price: low to high' },
  { value: 'PRICE_DESC', label: 'Price: high to low' },
  { value: 'DISTANCE', label: 'Distance' },
  { value: 'DELIVERY_ESTIMATE', label: 'Delivery estimate' },
  { value: 'POPULARITY', label: 'Popularity' },
];

function nextFilters(
  filters: AppliedFilters,
  key: string,
  value: string,
  checked: boolean,
): AppliedFilters {
  const values = filters[key] ?? [];
  const next = checked ? [...values, value] : values.filter((candidate) => candidate !== value);
  if (next.length === 0) {
    return Object.fromEntries(Object.entries(filters).filter(([candidate]) => candidate !== key));
  }
  return { ...filters, [key]: next };
}

function FilterContents({
  facets,
  value,
  onChange,
}: Pick<FilterPanelProps, 'facets' | 'value' | 'onChange'>) {
  return (
    <div style={{ display: 'grid', gap: space[1] }}>
      {facets.map((facet) => (
        <FilterGroup
          key={facet.key}
          legend={facet.label}
          name={facet.key}
          options={facet.options}
          selected={value[facet.key] ?? []}
          onChange={(option, checked) => onChange(nextFilters(value, facet.key, option, checked))}
        />
      ))}
    </div>
  );
}

export interface FilterPanelProps {
  facets: readonly SearchFacet[];
  value: AppliedFilters;
  sort: SearchSortOption;
  onChange: (filters: AppliedFilters) => void;
  onSortChange: (sort: SearchSortOption) => void;
  mobile?: boolean;
}

/** Controlled facets only: the component never manufactures a brand, colour, or availability value. */
export function FilterPanel({
  facets,
  value,
  sort,
  onChange,
  onSortChange,
  mobile = false,
}: FilterPanelProps) {
  const hasFilters = Object.values(value).some((values) => values.length > 0);
  const clear = () => onChange({});
  const chips = Object.entries(value).flatMap(([key, values]) =>
    values.map((filter) => ({ key, value: filter })),
  );
  const controls = (
    <>
      <label style={{ display: 'grid', gap: space[2], ...typeStyle('label') }}>
        Sort results
        <select
          aria-label="Sort results"
          value={sort}
          onChange={(event) => onSortChange(event.currentTarget.value as SearchSortOption)}
          style={{ minHeight: '44px', borderColor: semanticColor.borderDefault }}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {hasFilters ? (
        <Button variant="ghost" onClick={clear} aria-label="Clear all filters">
          Clear all
        </Button>
      ) : null}
      <FilterContents facets={facets} value={value} onChange={onChange} />
    </>
  );

  if (mobile) {
    return <MobileFilters controls={controls} chips={chips} onChange={onChange} value={value} />;
  }
  return (
    <aside aria-label="Search filters" style={{ display: 'grid', gap: space[3] }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[2] }}>
        {chips.map((chip) => (
          <Button
            key={`${chip.key}-${chip.value}`}
            variant="secondary"
            size="sm"
            aria-label={`Remove filter: ${chip.key} ${chip.value}`}
            onClick={(event) => {
              onChange(nextFilters(value, chip.key, chip.value, false));
              event.currentTarget.focus();
            }}
          >
            {chip.value} ×
          </Button>
        ))}
      </div>
      {controls}
    </aside>
  );
}

function MobileFilters({
  controls,
  chips,
  onChange,
  value,
}: {
  controls: ReactNode;
  chips: readonly { key: string; value: string }[];
  onChange: (filters: AppliedFilters) => void;
  value: AppliedFilters;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: 'grid', gap: space[3] }}>
      <Button variant="secondary" onClick={() => setOpen(true)} aria-haspopup="dialog">
        Filters
      </Button>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[2] }}>
        {chips.map((chip) => (
          <Button
            key={`${chip.key}-${chip.value}`}
            variant="secondary"
            size="sm"
            aria-label={`Remove filter: ${chip.key} ${chip.value}`}
            onClick={(event) => {
              onChange(nextFilters(value, chip.key, chip.value, false));
              event.currentTarget.focus();
            }}
          >
            {chip.value} ×
          </Button>
        ))}
      </div>
      <BottomSheet open={open} title="Filters" onClose={() => setOpen(false)}>
        {controls}
      </BottomSheet>
    </div>
  );
}
